import React from 'react';
import { Link } from 'react-router-dom';
import { PlayerStats } from './types';
import { COLUMNS, MAX_COL_WIDTH, MIN_COL_WIDTH } from './columns';
import { formatStat } from './helpers';

interface PlayerStatsTableProps {
  displayPlayers: PlayerStats[];
  playerMap: Record<number, string>;
  colWidths: Record<string, number>;
  tableWidth: number;
  sortBy: string;
  sortDesc: boolean;
  handleSort: (field: string) => void;
  startResize: (colKey: string, e: React.MouseEvent) => void;
  resizeColumn: (colKey: string, delta: number) => void;
}

const PlayerStatsTable: React.FC<PlayerStatsTableProps> = ({
  displayPlayers,
  playerMap,
  colWidths,
  tableWidth,
  sortBy,
  sortDesc,
  handleSort,
  startResize,
  resizeColumn,
}) => {
  return (
    <div className="overflow-x-auto">
      <table
        className="divide-y divide-gray-200 dark:divide-gray-700"
        style={{ tableLayout: 'fixed', width: tableWidth }}
      >
        <thead className="bg-gray-50 dark:bg-gray-800">
          <tr>
            {COLUMNS.map((col) => (
              <th
                key={col.key}
                className={`relative px-2 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase ${col.align === 'left' ? 'text-left' : 'text-center'}`}
                style={{ width: colWidths[col.key], overflow: 'hidden' }}
              >
                {col.sortField ? <button
                  type="button"
                  className="flex w-full items-center gap-1 overflow-hidden rounded-sm hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:hover:bg-gray-700"
                  onClick={() => handleSort(col.sortField!)}
                  aria-label={`Sort by ${col.label}${sortBy === col.sortField ? `, currently ${sortDesc ? 'descending' : 'ascending'}` : ''}`}
                >
                  <span className="truncate">{col.label}</span>
                  {sortBy === col.sortField && (
                    <span aria-hidden="true" className="text-blue-500 dark:text-blue-400 flex-shrink-0">
                      {sortDesc ? '↓' : '↑'}
                    </span>
                  )}
                </button> : <div className="flex items-center gap-1 overflow-hidden"><span className="truncate">{col.label}</span></div>}
                {/* Resize handle */}
                <div
                  className="absolute top-0 right-0 w-2 h-full cursor-col-resize group"
                  onMouseDown={(e) => startResize(col.key, e)}
                  onKeyDown={(event) => {
                    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
                    event.preventDefault();
                    event.stopPropagation();
                    resizeColumn(col.key, event.key === 'ArrowLeft' ? -10 : 10);
                  }}
                  role="separator"
                  tabIndex={0}
                  aria-label={`Resize ${col.label} column`}
                  aria-orientation="vertical"
                  aria-valuemin={MIN_COL_WIDTH}
                  aria-valuemax={MAX_COL_WIDTH}
                  aria-valuenow={colWidths[col.key]}
                >
                  <div className="absolute right-0 top-0 w-px h-full bg-gray-300 dark:bg-gray-600 group-hover:bg-blue-500 dark:group-hover:bg-blue-400 group-hover:w-0.5" />
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
          {displayPlayers.map((player) => {
            const t = player.stats?.total || {};
            const passAcc = t.passes && t.successfulPasses
              ? ((t.successfulPasses / t.passes) * 100).toFixed(0)
              : '-';
            const duelWinRate = t.duels && t.duelsWon
              ? ((t.duelsWon / t.duels) * 100).toFixed(0)
              : '-';

            const cellClass = 'px-2 py-2 text-sm text-center text-gray-900 dark:text-gray-100 overflow-hidden truncate';

            return (
              <tr key={player.playerId} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                {/* Player */}
                <td className="px-2 py-2 overflow-hidden" style={{ width: colWidths['player'] }}>
                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                    {playerMap[player.playerId] ? (
                      <Link
                        to={`/players/${playerMap[player.playerId]}`}
                        className="text-blue-600 dark:text-blue-400 hover:underline"
                      >
                        {player.shortName || player.name || 'Unknown player'}
                      </Link>
                    ) : (
                      player.shortName || player.name || 'Unknown player'
                    )}
                  </div>
                </td>
                {/* Pos */}
                <td className="px-2 py-2 text-xs text-gray-500 dark:text-gray-400 overflow-hidden truncate" style={{ width: colWidths['pos'] }}>
                  {player.position?.substring(0, 3) || '-'}
                </td>
                {/* Min */}
                <td className={cellClass} style={{ width: colWidths['min'] }}>
                  {formatStat(t.minutesOnField)}
                </td>
                {/* G */}
                <td className={`${cellClass} font-medium`} style={{ width: colWidths['goals'] }}>
                  {t.goals ? <span className="text-green-600 dark:text-green-400">{t.goals}</span> : '-'}
                </td>
                {/* A */}
                <td className={cellClass} style={{ width: colWidths['assists'] }}>
                  {t.assists ? <span className="text-blue-600 dark:text-blue-400">{t.assists}</span> : '-'}
                </td>
                {/* xG */}
                <td className={cellClass} style={{ width: colWidths['xg'] }}>
                  {formatStat(t.xgShot, 2)}
                </td>
                {/* Sh */}
                <td className={cellClass} style={{ width: colWidths['shots'] }}>
                  {formatStat(t.shots)}
                </td>
                {/* SoT */}
                <td className={cellClass} style={{ width: colWidths['sot'] }}>
                  {formatStat(t.shotsOnTarget)}
                </td>
                {/* Pass */}
                <td className={cellClass} style={{ width: colWidths['pass'] }}>
                  {formatStat(t.passes)}
                </td>
                {/* Cmp */}
                <td className={cellClass} style={{ width: colWidths['cmp'] }}>
                  <span title={`${passAcc}% accuracy`}>
                    {formatStat(t.successfulPasses)}
                  </span>
                </td>
                {/* KP */}
                <td className={cellClass} style={{ width: colWidths['kp'] }}>
                  {formatStat(t.keyPasses)}
                </td>
                {/* Duel */}
                <td className={cellClass} style={{ width: colWidths['duel'] }}>
                  {formatStat(t.duels)}
                </td>
                {/* Won */}
                <td className={cellClass} style={{ width: colWidths['won'] }}>
                  <span title={`${duelWinRate}% win rate`}>
                    {formatStat(t.duelsWon)}
                  </span>
                </td>
                {/* Int */}
                <td className={cellClass} style={{ width: colWidths['int'] }}>
                  {formatStat(t.interceptions)}
                </td>
                {/* Rec */}
                <td className={cellClass} style={{ width: colWidths['rec'] }}>
                  {formatStat(t.recoveries)}
                </td>
                {/* Cards */}
                <td className="px-2 py-2 text-sm text-center overflow-hidden" style={{ width: colWidths['cards'] }}>
                  <div className="flex items-center justify-center gap-1">
                    {t.yellowCards ? (
                      <span className="inline-block w-3 h-4 bg-yellow-400 rounded-sm" title="Yellow card" />
                    ) : null}
                    {t.redCards ? (
                      <span className="inline-block w-3 h-4 bg-red-500 rounded-sm" title="Red card" />
                    ) : null}
                    {!t.yellowCards && !t.redCards && <span className="text-gray-900 dark:text-gray-100">-</span>}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default PlayerStatsTable;
